<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { api, type HealthInfo } from "@/lib/api.js";
import { currentUser, endSession } from "@/lib/session.js";

/**
 * Phase 1 put the engine state in the header on purpose — staff could see which
 * model answered and whether vision was on (reference §11). Keeping that visible
 * is a trust feature, so mAImu shows the model plus which data sources are live
 * versus mocked.
 */
const health = ref<HealthInfo | null>(null);

/** Which view the app is showing. Only admins can switch — the queue and the admin
 *  panel are theirs (§11, D18). */
const view = defineModel<"chat" | "unanswered" | "admin" | "cases">("view", { required: true });

const isAdmin = computed(() => currentUser.value?.role === "admin");
const openQuestions = ref(0);

onMounted(async () => {
  try {
    health.value = await api.health();
  } catch {
    health.value = null;
  }

  if (!isAdmin.value) return;
  try {
    openQuestions.value = (await api.unansweredStats()).open;
  } catch {
    openQuestions.value = 0;
  }
});
</script>

<template>
  <header class="header">
    <div class="brand">
      <p class="eyebrow">Sisemine tööriist</p>
      <h1>mAImu</h1>
    </div>

    <div class="pills">
      <template v-if="health">
        <span class="pill" :class="{ warn: !health.llm.configured }">
          {{ health.llm.configured ? `Mudel: ${health.llm.model}` : "Mudel: seadistamata" }}
        </span>
        <span class="pill" :class="{ warn: health.tools.jahk === 'mock' }">
          JAHK: {{ health.tools.jahk === "mock" ? "testandmed" : "elav" }}
        </span>
        <span class="pill" :class="{ warn: health.tools.directo === 'mock' }">
          Directo: {{ health.tools.directo === "mock" ? "testandmed" : "elav" }}
        </span>
        <span class="pill" :class="{ warn: health.tools.shopify === 'mock' }">
          Shopify: {{ health.tools.shopify === "mock" ? "testandmed" : "elav" }}
        </span>
        <span class="pill" :class="{ warn: !health.knowledge?.chunks }">
          Teadmus:
          {{ health.knowledge?.chunks ? `${health.knowledge.sources} dokumenti` : "puudub" }}
        </span>
        <span class="pill">Tööriistaringe: {{ health.tools.maxRounds }}</span>
      </template>
      <!-- Outside the health block on purpose: a failed /health call must not take
           the admin's way into the queue or the panel with it. -->
      <!-- Toimikud are everyone's, not an admin surface: the point is that two people
           can work one problem together (D21). -->
      <button
        class="pill link"
        :class="{ on: view === 'cases' }"
        type="button"
        @click="view = view === 'cases' ? 'chat' : 'cases'"
      >
        Toimikud
      </button>
      <template v-if="isAdmin">
        <button
          class="pill link"
          :class="{ on: view === 'unanswered' }"
          type="button"
          @click="view = view === 'unanswered' ? 'chat' : 'unanswered'"
        >
          Vastamata{{ openQuestions ? ` (${openQuestions})` : "" }}
        </button>
        <button
          class="pill link"
          :class="{ on: view === 'admin' }"
          type="button"
          @click="view = view === 'admin' ? 'chat' : 'admin'"
        >
          Haldus
        </button>
        <button v-if="view !== 'chat'" class="pill link" type="button" @click="view = 'chat'">
          Tagasi vestlusesse
        </button>
      </template>
      <span v-if="currentUser" class="pill user">{{ currentUser.username }}</span>
      <button class="btn-ghost" type="button" @click="endSession()">Logi välja</button>
    </div>
  </header>
</template>

<style scoped>
.header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.5rem 0.85rem;
  border-bottom: 3px solid var(--gold);
  background: var(--card);
}

.eyebrow {
  margin: 0;
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gold-dark);
}

h1 {
  margin: 0;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
}

.pills {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.pill.warn {
  color: var(--gold-dark);
  border-color: var(--gold);
}

.pill.user {
  color: var(--steel);
  font-weight: 600;
}

/* The queue is a button, but it belongs to the pill row visually. */
.pill.link {
  font: inherit;
  cursor: pointer;
  color: var(--green);
  border-color: var(--green);
  background: transparent;
}

.pill.link:hover {
  background: var(--soft);
}

/* The view you are already in, so the row reads as navigation and not as a set of
   identical buttons. */
.pill.link.on {
  background: var(--soft);
  font-weight: 600;
}
</style>
